/**
 * Analytics - one place that knows how events reach Google.
 *
 * Everything on the site goes through `track()`. If the site ever moves to
 * Google Tag Manager, this single function changes to a `dataLayer.push` and
 * nothing else in the codebase has to be touched.
 *
 * The gtag.js snippet lives in public/index.html so the tag is present in the
 * raw HTML of every prerendered page - but it is configured with
 * `send_page_view: false`, because a single-page app changes URL without
 * reloading and gtag's automatic page_view would only ever see the first one.
 * Page views are sent from here instead, on the initial load and on every
 * route change.
 */

export const GA_MEASUREMENT_ID = 'G-XM8EJ1TYG8';

/* ── Marking your own visits ───────────────────────────────────────────────
   GA4 can exclude internal traffic by IP, but a home or mobile IP changes
   without warning and the filter then silently stops working - and it never
   covers you browsing from a phone or someone else's network.

   Instead this tags the *browser*. Visit any page with `?wdf_internal=1` once
   and every event from that browser afterwards carries
   `traffic_type=internal`, which is exactly what GA4's built-in Internal
   Traffic data filter looks for. Survives IP changes, follows you across
   devices you flag, and affects nobody else.

   `?wdf_internal=0` clears it. Wrapped in try/catch because localStorage
   throws outright in some privacy modes.
   ────────────────────────────────────────────────────────────────────────── */
const INTERNAL_KEY = 'wdf_internal_traffic';

const resolveInternal = () => {
  if (typeof window === 'undefined') return false;
  try {
    const flag = new URLSearchParams(window.location.search).get('wdf_internal');
    if (flag !== null) {
      if (flag === '0' || flag === 'false') window.localStorage.removeItem(INTERNAL_KEY);
      else window.localStorage.setItem(INTERNAL_KEY, '1');
    }
    return window.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch {
    return false;
  }
};

const isInternal = resolveInternal();

/** True when this browser has been flagged as yours. Exposed for debugging. */
export const isInternalTraffic = () => isInternal;

/* Only the live site reports - the same hostname guard as the gtag config in
   public/index.html, so localhost, previews and the host's temporary domains
   never reach the property. */
const isLiveSite =
  typeof window !== 'undefined' && /^(www\.)?webdevf\.com$/.test(window.location.hostname);

/** Safe accessor - gtag is absent during prerendering and when blocked. */
const gtag = (...args) => {
  if (!isLiveSite || typeof window.gtag !== 'function') return;
  window.gtag(...args);
};

/**
 * Send a GA4 event.
 *
 * Keep parameter values low-cardinality and human-readable: they become the
 * dimensions you group by in reports, and free-form text makes a report
 * unreadable. Names are snake_case by GA4 convention.
 */
export const track = (name, params = {}) => {
  gtag('event', name, {
    ...params,
    page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...(isInternal && { traffic_type: 'internal' }),
  });
};

/**
 * Page view. Called on first paint and on every client-side route change, so
 * "Pages and screens" reflects real navigation rather than only entry pages.
 */
export const pageview = (path) => {
  if (typeof window === 'undefined') return;
  gtag('event', 'page_view', {
    page_path: path || window.location.pathname,
    page_location: window.location.href,
    page_title: document.title,
    ...(isInternal && { traffic_type: 'internal' }),
  });
};

/** Pull readable text out of a button's children for use as an event label. */
export const labelFrom = (children, fallback = 'button') => {
  const walk = (node) => {
    if (node === null || node === undefined || node === false) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(walk).join('');
    if (node.props && node.props.children) return walk(node.props.children);
    return '';
  };
  const text = walk(children).replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, 80) : fallback;
};

/**
 * Where on the page a click happened.
 *
 * Prefers an explicit `data-section`, then a section id, and falls back to the
 * text of the nearest enclosing section's heading - so a new section gets a
 * meaningful label in GA4 without anyone remembering to tag it. `#root` is
 * skipped deliberately: it wraps the whole app, so matching it would label
 * every click on the site "root".
 */
const IGNORED_IDS = new Set(['root']);

export const sectionOf = (el) => {
  if (!el || typeof el.closest !== 'function') return 'unknown';

  let node = el.closest('[data-section], section[id], section, [id]');
  while (node) {
    const named = node.getAttribute('data-section') || node.id;
    if (named && !IGNORED_IDS.has(named)) return named;

    if (node.tagName === 'SECTION') {
      const heading = node.querySelector('h1, h2');
      if (heading) {
        const text = heading.textContent.replace(/\s+/g, ' ').trim();
        if (text) return text.slice(0, 60);
      }
    }

    const parent = node.parentElement;
    node = parent && parent.closest('[data-section], section[id], section, [id]');
  }
  return 'unknown';
};

/**
 * A call-to-action was clicked. This is the event that answers "which button,
 * on which page, in which section".
 */
export const trackCta = (event, label, extra = {}) => {
  track('cta_click', {
    cta_label: label,
    cta_section: sectionOf(event && event.currentTarget),
    ...extra,
  });
};

/* ── Lead funnel ──────────────────────────────────────────────────────────
   Named steps so GA4's funnel exploration can show where people drop out
   between opening the form and submitting it. */

export const trackLeadFormOpen = (service, source) =>
  track('lead_form_open', { service: service || 'unspecified', form_source: source });

export const trackLeadFormStep = (stepNumber, stepName, service) =>
  track('lead_form_step', {
    step_number: stepNumber,
    step_name: stepName,
    service: service || 'unspecified',
  });

/** GA4's recommended lead event - mark this one as a key event in GA4. */
export const trackLeadSubmit = (service, source) =>
  track('generate_lead', { service: service || 'unspecified', form_source: source });

export const trackLeadError = (service, reason) =>
  track('lead_form_error', { service: service || 'unspecified', error_reason: reason });

/** Phone, email, WhatsApp - the conversions that never touch a form. */
export const trackContactClick = (method, location) =>
  track('contact_click', { contact_method: method, contact_location: location });

/**
 * Copying the email address or phone number, anywhere on the site. The first
 * real lead read the address off the contact page and emailed it, which left
 * no event at all. One document-level listener covers the footer, the contact
 * page and every popup, rather than an onCopy on each element.
 */
const EMAIL = 'contact@webdevf.com';
const PHONE_DIGITS = '524818924';

export const initCopyTracking = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('copy', () => {
    const selection = document.getSelection();
    const text = selection ? selection.toString() : '';
    if (!text) return;
    const origin = selection.anchorNode && selection.anchorNode.parentElement;
    if (origin && origin.closest('[data-copy-tracked]')) return; // tracked by DirectContact
    const where = sectionOf(origin);
    if (text.includes(EMAIL)) trackContactClick('email_copy', where);
    else if (text.replace(/\D/g, '').includes(PHONE_DIGITS)) trackContactClick('phone_copy', where);
  });
};

export const trackOutbound = (url) => {
  let domain = '';
  try { domain = new URL(url).hostname; } catch { domain = 'unknown'; }
  track('outbound_click', { link_url: url.slice(0, 200), link_domain: domain });
};
